import { useTranslation } from 'react-i18next';
import { TextInput, Button, Textarea, Loader, Select, SelectProps, Group, NumberInput } from '@mantine/core';
import {
    IconTrash,
    IconLock,
    IconBuildingStore,
    IconCheck,
    IconGitMerge,
} from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import EditAttrsModal from '@/components/edit-attrs-modal';
import { useState, useEffect } from 'react';
import { localTenant } from '@/services/session-service';
import type { UseMutationResult } from '@tanstack/react-query';
import { useDeleteBusiness } from '@/hooks/api-hooks';
import { BUSINESSES_PATH, navigate } from '@/lib/navigation';
import BusinessContact from './business-contact';
import { SELECT_BUSINESS_CATEGORIES } from '@/types/templates';
import { TAG_ICONS } from '@/lib/tag-utils';
import MergeBusinessModal from '@/components/merge-business-modal';
import ShowMetadata from './show-metadata';

interface Props {
    business: Business;
    updateMutation: UseMutationResult<any, Error, Partial<Business>, unknown>;
}

export default function EditBusinessDetails({
    business,
    updateMutation
}: Props): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    const deleteBusiness = useDeleteBusiness();
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

    // Permissions
    const isOwner = tenant && business && business.owner_id === tenant.id;
    const isManager = tenant && business && business.manager_id === tenant.id;
    const canEditBusiness = isOwner || isManager;

    // State for Business editing
    const [businessDraft, setBusinessDraft] = useState<Business>(business);
    const [businessBackup, setBusinessBackup] = useState<Business>(business);

    // Loading states
    const [savingSections, setSavingSections] = useState<Record<string, boolean>>({});

    // Initialize state when data loads
    useEffect(() => {
        setBusinessDraft(business);
        setBusinessBackup(business);
    }, [business]);

    const save = (draft: Business, section: string = 'info') => {
        setSavingSections(prev => ({ ...prev, [section]: true }));
        updateMutation.mutate(draft, {
            onSuccess: () => {
                setBusinessDraft({ ...draft });
                setBusinessBackup({ ...draft });
                setSavingSections(prev => ({ ...prev, [section]: false }));
            },
            onError: () => {
                setSavingSections(prev => ({ ...prev, [section]: false }));
            }
        });
    };

    const businessHasChanges = () => {
        return businessDraft?.name !== businessBackup?.name ||
            businessDraft?.category !== businessBackup?.category ||
            businessDraft?.tax_id_type !== businessBackup?.tax_id_type ||
            businessDraft?.tax_name !== businessBackup?.tax_name ||
            businessDraft?.tax_id_number !== businessBackup?.tax_id_number ||
            businessDraft?.tax_condition !== businessBackup?.tax_condition;
    };

    const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => {
        const Icon = TAG_ICONS[option.value as keyof typeof TAG_ICONS];
        return (
            <Group flex="1" gap="xs">
                {Icon && <Icon size={16} stroke={1.5} style={{ opacity: 0.6 }} />}
                {option.label}
                {checked && <IconCheck size={16} stroke={1.5} style={{ marginInlineStart: 'auto', opacity: 0.6 }} />}
            </Group>
        );
    };
    const SelectedIcon = businessDraft.category ? (TAG_ICONS[businessDraft.category as keyof typeof TAG_ICONS]) : null;

    return (
        <div className="space-y-4 max-w-4xl">
            {!canEditBusiness && (
                <div className="solid-box-border-warning flex items-center justify-center gap-2 p-3 text-xs text-amber-700 bg-amber-50/50 rounded-lg">
                    <IconLock size={16} className="text-amber-600 shrink-0" />
                    <span>{t('read_only')}</span>
                </div>
            )}
            {isOwner && (
                <div className="solid-box-border-green flex items-center justify-center gap-2 p-3 text-xs text-emerald-700 bg-emerald-50/50 rounded-lg">
                    <IconBuildingStore size={16} className="text-emerald-600 shrink-0" />
                    <span className="font-semibold">{t('you_are_the_owner')}</span>
                </div>
            )}

            {/* General Information */}
            <div className="space-y-4 solid-box-border h-fit flex-1 w-full min-w-0">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 sm:gap-3">
                    <Select
                        label={t('category')}
                        value={businessDraft.category}
                        data={SELECT_BUSINESS_CATEGORIES()}
                        onChange={(value) => setBusinessDraft({ ...businessDraft, category: value })}
                        className="font-semibold w-full sm:w-48 shrink-0"
                        searchable
                        renderOption={renderSelectOption}
                        leftSection={SelectedIcon ? <SelectedIcon size={16} stroke={1.5} /> : null}
                    />
                    <TextInput
                        className="font-semibold flex-1 min-w-0"
                        label={t('name')}
                        value={businessDraft.name || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, name: e.target.value })}
                        disabled={!canEditBusiness}
                    />
                </div>
                <TextInput
                    className='font-semibold'
                    label={t('tax_name')}
                    value={businessDraft.tax_name || ''}
                    onChange={(e) => setBusinessDraft({ ...businessDraft, tax_name: e.target.value })}
                    disabled={!canEditBusiness}
                />
                <div className='flex flex-wrap gap-2'>
                    <Select
                        className='font-semibold'
                        label={t('tax_id_type')}
                        data={[
                            { value: 'CUIL', label: 'CUIL' },
                            { value: 'CUIT', label: 'CUIT' },
                            { value: 'DNI', label: 'DNI' },
                            { value: 'Pasaporte', label: 'Pasaporte' },
                            { value: 'Otro', label: 'Otro' }
                        ]}
                        value={businessDraft.tax_id_type}
                        onChange={(value) => setBusinessDraft({ ...businessDraft, tax_id_type: value })}
                        disabled={!canEditBusiness}
                    />
                    <TextInput
                        className='font-semibold'
                        label={t('id_number')}
                        value={businessDraft.tax_id_number || ''}
                        onChange={(e) => setBusinessDraft({ ...businessDraft, tax_id_number: e.target.value })}
                        disabled={!canEditBusiness}
                    />
                    <Select
                        className='font-semibold'
                        label={t('tax_condition')}
                        data={[
                            { value: '1', label: 'IVA Responsable Inscripto' },
                            { value: '4', label: 'IVA Sujeto Exento' },
                            { value: '5', label: 'Consumidor Final' },
                            { value: '6', label: 'Responsable Monotributo' },
                            { value: '8', label: 'Sujeto No Categorizado' },
                            { value: '9', label: 'Proveedor del Exterior' },
                            { value: '10', label: 'Cliente del Exterior' },
                            { value: '11', label: 'IVA Liberado - Ley Nº 19.640' },
                            { value: '13', label: 'Monotributista Social' },
                            { value: '15', label: 'IVA No Alcanzado' },
                            { value: '17', label: 'Monotributista Trabajador Independiente Promovido' }
                        ]}
                        value={businessDraft.tax_condition || ''}
                        onChange={(value) => setBusinessDraft({ ...businessDraft, tax_condition: value })}
                        disabled={!canEditBusiness}
                        clearable
                    />
                </div>
                {canEditBusiness && (
                    <div className="flex justify-end mt-8">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            onClick={() => save(businessDraft, 'info')}
                            loading={savingSections.info}
                            disabled={!businessHasChanges()}
                        >
                            {t('save')}
                        </Button>
                    </div>
                )}
            </div>

            <EditAttrsModal
                attrType='self_attrs'
                title={t('attributes')}
                attrs={businessDraft.self_attrs}
                item={businessDraft}
                onChange={(attrs) => {
                    if (canEditBusiness) {
                        save({ ...businessDraft, self_attrs: attrs });
                    }
                }}
                isSaving={savingSections.info}
            />

            {/* Contacts & Location */}
            <BusinessContact business={business} updateMutation={updateMutation} />

            {/* Observations Section */}
            <div className="solid-box-border">
                <h3 className="text-md font-bold mb-4">
                    {t('observations')}
                </h3>
                <Textarea
                    value={businessDraft.observations || ''}
                    onChange={(e) => setBusinessDraft({ ...businessDraft, observations: e.target.value })}
                    minRows={4}
                    autosize
                    resize='vertical'
                    disabled={!canEditBusiness}
                />
                {canEditBusiness && (
                    <div className="flex justify-end mt-4">
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            onClick={() => save(businessDraft, 'observations')}
                            loading={savingSections.observations}
                            disabled={businessDraft.observations === businessBackup.observations}
                        >
                            {t('save')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Danger Zone Section */}
            {(isOwner || isManager) && (
                <div className={`solid-box-border ${isOwner ? 'border-red-200 bg-red-50/20' : 'border-gray-200 bg-gray-50/20'}`}>
                    <h4 className="text-xs font-bold text-red-700 mb-1">{t('danger_zone')}</h4>
                    <p className="text-xxs text-gray-500 mb-3">{t('delete_business_warning')}</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            size='xs'
                            color='red'
                            variant='outline'
                            leftSection={<IconTrash size={16} />}
                            loading={deleteBusiness.isPending}
                            onClick={() => {
                                if (!confirm(t('confirm_delete_business'))) return;
                                deleteBusiness.mutate(Number(business.id), {
                                    onSuccess: () => {
                                        navigate(BUSINESSES_PATH);
                                    },
                                })
                            }}
                        >
                            <span className="font-semibold">{t('delete')} {t('business')}</span>
                        </Button>
                        <Button
                            size='xs'
                            color='blue'
                            variant='outline'
                            leftSection={<IconGitMerge size={16} />}
                            onClick={() => setIsMergeModalOpen(true)}
                        >
                            <span className="font-semibold">{t('merge_business')}</span>
                        </Button>
                    </div>
                </div>
            )}

            <MergeBusinessModal
                opened={isMergeModalOpen}
                onClose={() => setIsMergeModalOpen(false)}
                targetBusiness={business}
            />

            <ShowMetadata item={business} />
        </div>
    );
}